import { useEffect, useRef, useState, useMemo } from 'react';
import { History, TrendingUp } from 'lucide-react';

// Utils
import { logger } from '../../utils/logger';
import { TIMEOUTS, LAYOUT } from '../../constants/uiConstants';

// Import UI components
import Badge from '../ui/Badge';
import Card from '../ui/Card';
import PlayerAvatar from '../ui/PlayerAvatar';
import Tooltip from '../ui/Tooltip';

// Import bread calculations
import {
  calculateLifetimeTotal,
  calculateOutstandingDebt,
  calculateDebtRank,
} from '../../utils/calculations/breadCalculations';

const BreadTrackerTab = ({
  players: _players = [], // Renamed to indicate it's intentionally unused
  playerStats = {},
  breadLogs = [],
  handleMarkPaid,
  breadSortField = 'sliceValue',
  breadSortDirection = 'desc',
  setBreadSortField,
  setBreadSortDirection,
  setSelectedPlayer,
  setActiveTab,
  isAdmin = false,
}) => {
  const outstandingDebtsRef = useRef(null);
  const [debtsCardHeight, setDebtsCardHeight] = useState(
    LAYOUT.CARD_MIN_HEIGHT
  );
  const [recentActivityId, setRecentActivityId] = useState(null);
  const [processingIds, setProcessingIds] = useState(new Set());

  // Keep track of latest breadLogs for async access
  const breadLogsRef = useRef(breadLogs);
  useEffect(() => {
    breadLogsRef.current = breadLogs;
  }, [breadLogs]);

  // Handle recent activity highlight auto-hide
  useEffect(() => {
    if (recentActivityId) {
      const timer = setTimeout(() => {
        setRecentActivityId(null);
      }, 3000); // 3 second highlight duration
      return () => clearTimeout(timer);
    }
  }, [recentActivityId]);

  // Wrapper for handleMarkPaid to show highlight effect
  const handleMarkPaidWithNotification = async (playerId, type) => {
    const actionId = `${playerId}-${type}`;
    if (processingIds.has(actionId)) return;

    try {
      setProcessingIds(prev => new Set(prev).add(actionId));
      await handleMarkPaid(playerId, type);
      
      // Wait a bit for data to be reloaded, then find the most recent entry to highlight
      setTimeout(() => {
        try {
          const currentBreadLogs = breadLogsRef.current || [];
          logger.debug('Available bread logs:', currentBreadLogs.map(log => ({
            id: log.id, 
            playerId: log.playerId, 
            playerName: log.playerName,
            timestamp: log.timestamp,
            type: log.type
          })));
          
          // Find the most recent entry for this player
          const playerLogs = currentBreadLogs.filter(log => 
            log.playerId === playerId || log.consumerId === playerId
          );
          
          logger.debug(`Found ${playerLogs.length} logs for player ${playerId}`);
          
          const mostRecentLog = playerLogs
            .sort((a, b) => {
              const dateA = new Date(a.timestamp || a.createdAt || 0);
              const dateB = new Date(b.timestamp || b.createdAt || 0);
              return dateB - dateA;
            })[0];
          
          if (mostRecentLog) {
            logger.debug('Highlighting recent activity:', {
              id: mostRecentLog.id,
              playerName: mostRecentLog.playerName,
              timestamp: mostRecentLog.timestamp,
              type: mostRecentLog.type
            });
            setRecentActivityId(mostRecentLog.id);
          } else {
            logger.warn('No recent log found for player:', playerId);
          }
        } catch (innerError) {
          logger.error('Error in highlight timeout:', innerError);
        }
      }, 800); // Increased wait time to ensure data is fully reloaded
    } catch (error) {
      logger.error('Error marking payment:', error);
    } finally {
      // Small delay before enabling button again to prevent double-clicks
      setTimeout(() => {
        setProcessingIds(prev => {
          const next = new Set(prev);
          next.delete(actionId);
          return next;
        });
      }, 500);
    }
  };

  useEffect(() => {
    const updateHeight = () => {
      if (outstandingDebtsRef.current) {
        const height = outstandingDebtsRef.current.offsetHeight;
        logger.debug('Outstanding Debts height:', height);
        setDebtsCardHeight(Math.max(LAYOUT.CARD_MIN_HEIGHT, height));
      }
    };

    // Multiple attempts to get the correct height
    setTimeout(updateHeight, TIMEOUTS.HEIGHT_UPDATE_FAST);
    setTimeout(updateHeight, TIMEOUTS.HEIGHT_UPDATE_MEDIUM);
    setTimeout(updateHeight, TIMEOUTS.HEIGHT_UPDATE_SLOW);

    const resizeObserver = new ResizeObserver(() => {
      setTimeout(updateHeight, TIMEOUTS.RESIZE_OBSERVER_DELAY);
    });

    if (outstandingDebtsRef.current) {
      resizeObserver.observe(outstandingDebtsRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [playerStats]);

  // Memoize expensive debt calculations
  const playersWithDebt = useMemo(() => {
    return Object.values(playerStats)
      .filter(p => {
        const debt = calculateOutstandingDebt(p);
        return debt.total > 0;
      })
      .sort((a, b) => calculateLifetimeTotal(b) - calculateLifetimeTotal(a));
  }, [playerStats]);

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-bold tracking-tight text-zinc-900">
          The Bread Debt Tracker
        </h2>
        {isAdmin && (
          <Badge type="admin">Admin Access</Badge>
        )}
      </div>

      {/* MAIN CONTENT */}
      <div className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Outstanding Debts Table */}
          <div ref={outstandingDebtsRef} className="lg:col-span-2">
            <Card className="overflow-hidden border-zinc-200 h-fit">
              <div className="p-4 border-b border-zinc-200 bg-zinc-100/30 font-bold text-sm text-zinc-900 uppercase tracking-widest">
                Outstanding Debts
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-widest text-zinc-600 border-b border-zinc-200 bg-zinc-50">
                      <th className="py-3 px-6 font-bold">Player</th>
                      <th className="py-3 px-6 font-bold text-right text-zinc-900">
                        🍞 Full Slices
                      </th>
                      <th className="py-3 px-6 font-bold text-right text-zinc-900">
                        🥪 Half Slices
                      </th>
                      <th className="py-3 px-6 font-bold text-right text-zinc-900">
                        Lifetime Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="text-sm bg-white">
                    {playersWithDebt.map(p => {
                      const debt = calculateOutstandingDebt(p);
                      const netFull = debt.fullSlices;
                      const netHalf = debt.halfSlices;

                      return (
                        <tr
                          key={p.id}
                          className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50/50 transition-colors"
                        >
                          <td className="py-4 px-6 font-bold text-zinc-900">
                            <button
                              onClick={() => {
                                setSelectedPlayer(p);
                                setActiveTab('analytics');
                              }}
                              className="flex items-center gap-2 hover:text-indigo-600 transition-colors cursor-pointer"
                            >
                              <PlayerAvatar initials={p.initials} size="sm" />
                              {p.name}
                            </button>
                          </td>
                          <td className="py-4 px-6 text-right">
                            <div className="flex items-center justify-end gap-3">
                              <span className="font-mono font-bold text-lg text-zinc-950">
                                {Math.max(0, netFull)}
                              </span>
                              {isAdmin && netFull > 0 && (
                                <button
                                  onClick={() => handleMarkPaidWithNotification(p.id, 'full')}
                                  disabled={processingIds.has(`${p.id}-full`)}
                                  className={`px-2 py-1 rounded text-[10px] font-bold transition-colors border ${
                                    processingIds.has(`${p.id}-full`)
                                      ? 'bg-zinc-100 text-zinc-400 border-zinc-200 cursor-not-allowed'
                                      : 'bg-zinc-100 hover:bg-zinc-200 text-zinc-800 border-zinc-200'
                                  }`}
                                >
                                  {processingIds.has(`${p.id}-full`) ? '...' : 'ATE'}
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="py-4 px-6 text-right">
                            <div className="flex items-center justify-end gap-3">
                              <span className="font-mono font-bold text-lg text-zinc-900/80">
                                {Math.max(0, netHalf)}
                              </span>
                              {isAdmin && netHalf > 0 && (
                                <button
                                  onClick={() => handleMarkPaidWithNotification(p.id, 'half')}
                                  disabled={processingIds.has(`${p.id}-half`)}
                                  className={`px-2 py-1 rounded text-[10px] font-bold transition-colors border ${
                                    processingIds.has(`${p.id}-half`)
                                      ? 'bg-zinc-100 text-zinc-400 border-zinc-200 cursor-not-allowed'
                                      : 'bg-zinc-100 hover:bg-zinc-200 text-zinc-800 border-zinc-200'
                                  }`}
                                >
                                  {processingIds.has(`${p.id}-half`) ? '...' : 'ATE'}
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="py-4 px-6 text-right">
                            <div className="text-sm font-mono text-zinc-600">
                              {(
                                p.lifetimeFullSlices * 1.0 +
                                p.lifetimeHalfSlices * 0.5
                              ).toFixed(1)}{' '}
                              slices
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {Object.values(playerStats).filter(
                      p =>
                        p.earnedFullSlices - p.paidFullSlices > 0 ||
                        p.earnedHalfSlices - p.paidHalfSlices > 0
                    ).length === 0 && (
                      <tr className="bg-zinc-50">
                        <td
                          colSpan="4"
                          className="p-8 text-center text-zinc-400 italic"
                        >
                          No active debts! Everyone is fed.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>

          {/* BREAD CONSUMPTION LOG */}
          <Card
            className="overflow-hidden border-zinc-200 flex flex-col"
            style={{ height: `${debtsCardHeight}px` }}
          >
            {/* Debug info */}
            {process.env.NODE_ENV === 'development' && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  background: 'red',
                  color: 'white',
                  fontSize: '10px',
                  padding: '2px',
                }}
              >
                H: {debtsCardHeight}px
              </div>
            )}
            <div className="p-4 border-b border-zinc-100 bg-zinc-50 font-bold text-sm text-zinc-500 uppercase tracking-widest flex items-center gap-2">
              <History className="w-4 h-4" /> Recent Bread Activity
            </div>
            <div className="flex-1 overflow-y-auto">
              {breadLogs.length === 0 ? (
                <div className="p-6 text-center text-zinc-400 text-xs italic">
                  No bread consumption recorded yet.
                </div>
              ) : (
                <div className="divide-y divide-zinc-50">
                  {breadLogs
                    .sort(
                      (a, b) =>
                        new Date(b.timestamp || b.createdAt) -
                        new Date(a.timestamp || a.createdAt)
                    )
                    .map(log => {
                      // Check if this entry should be highlighted
                      const isHighlighted = recentActivityId === log.id;
                      
                      return (
                        <div
                          key={log.id}
                          className={`relative rounded-lg transition-all duration-300 group ${
                            isHighlighted 
                              ? 'p-[2px] bg-gradient-to-r from-indigo-500 via-purple-500 to-blue-500 bg-[length:200%_100%] animate-shimmer shadow-[0_0_20px_rgba(99,102,241,0.5)]' 
                              : 'p-0 hover:bg-zinc-50'
                          }`}
                        >
                          <div className={`p-3 text-xs flex flex-col gap-1 rounded-md h-full w-full ${
                            isHighlighted ? 'bg-white' : ''
                          }`}>
                            <div className="flex justify-between">
                              <span className={`font-bold ${
                                isHighlighted ? 'text-indigo-900' : 'text-zinc-900'
                              }`}>
                                {log.playerName}
                              </span>
                              <span className={`font-medium ${
                                isHighlighted ? 'text-indigo-700' : 'text-zinc-600'
                              }`}>
                                {log.type}
                              </span>
                            </div>
                            <div className="flex justify-between text-zinc-500 text-[10px]">
                              <Tooltip content={log.adminEmail || log.adminName} side="bottom">
                                <span className="font-medium">
                                  Logged by {log.adminName}
                                </span>
                              </Tooltip>
                              <span className="font-medium">
                                {log.timestampDisplay ||
                                  (log.timestamp
                                    ? `${new Date(log.timestamp)
                                        .toLocaleDateString('en-GB', {
                                          day: '2-digit',
                                          month: 'short',
                                          year: 'numeric',
                                          timeZone: 'Asia/Hong_Kong',
                                        })
                                        .replace(/,/g, '')}, ${new Date(
                                        log.timestamp
                                      ).toLocaleTimeString('en-GB', {
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        hour12: false,
                                        timeZone: 'Asia/Hong_Kong',
                                      })} HKT`
                                    : 'Just now')}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* WHO ATE THE MOST - FULL WIDTH */}
        <Card className="overflow-hidden border-zinc-200 h-fit">
          <div className="p-4 border-b border-zinc-100 bg-zinc-50 font-bold text-sm text-zinc-500 uppercase tracking-widest flex items-center gap-2">
            <TrendingUp className="w-4 h-4" /> Bread Leaderboard
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-zinc-600 border-b border-zinc-200 bg-zinc-50">
                  <th
                    className="py-2 px-4 font-bold cursor-pointer hover:bg-zinc-100 transition-colors"
                    onClick={() => {
                      if (breadSortField === 'rank') {
                        setBreadSortDirection(
                          breadSortDirection === 'asc' ? 'desc' : 'asc'
                        );
                      } else {
                        setBreadSortField('rank');
                        setBreadSortDirection('asc');
                      }
                    }}
                  >
                    Rank{' '}
                    {breadSortField === 'rank' &&
                      (breadSortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="py-2 px-4 font-bold">Player</th>
                  <th
                    className="py-2 px-4 font-bold text-right cursor-pointer hover:bg-zinc-100 transition-colors"
                    onClick={() => {
                      if (breadSortField === 'outstanding') {
                        setBreadSortDirection(
                          breadSortDirection === 'asc' ? 'desc' : 'asc'
                        );
                      } else {
                        setBreadSortField('outstanding');
                        setBreadSortDirection('desc');
                      }
                    }}
                  >
                    Outstanding{' '}
                    {breadSortField === 'outstanding' &&
                      (breadSortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th
                    className="py-2 px-4 font-bold text-right cursor-pointer hover:bg-zinc-100 transition-colors"
                    onClick={() => {
                      if (breadSortField === 'sliceValue') {
                        setBreadSortDirection(
                          breadSortDirection === 'asc' ? 'desc' : 'asc'
                        );
                      } else {
                        setBreadSortField('sliceValue');
                        setBreadSortDirection('desc');
                      }
                    }}
                  >
                    Lifetime Total{' '}
                    {breadSortField === 'sliceValue' &&
                      (breadSortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th
                    className="py-2 px-4 font-bold text-right cursor-pointer hover:bg-zinc-100 transition-colors"
                    onClick={() => {
                      if (breadSortField === 'clearRate') {
                        setBreadSortDirection(
                          breadSortDirection === 'asc' ? 'desc' : 'asc'
                        );
                      } else {
                        setBreadSortField('clearRate');
                        setBreadSortDirection('desc');
                      }
                    }}
                  >
                    Clear Rate{' '}
                    {breadSortField === 'clearRate' &&
                      (breadSortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                </tr>
              </thead>
              <tbody className="text-xs bg-white">
                {Object.values(playerStats)
                  .filter(p => (p.wins + p.losses) > 0)
                  .map(player => {
                    const totalSlices =
                      player.lifetimeFullSlices + player.lifetimeHalfSlices;
                    const sliceValue = calculateLifetimeTotal(player);
                    const outstanding =
                      Math.max(
                        0,
                        player.earnedFullSlices - player.paidFullSlices
                      ) *
                        1.0 +
                      Math.max(
                        0,
                        player.earnedHalfSlices - player.paidHalfSlices
                      ) *
                        0.5;
                    const clearRate =
                      sliceValue > 0
                        ? Math.round(
                            ((sliceValue - outstanding) / sliceValue) * 100
                          )
                        : 100;
                    return {
                      ...player,
                      totalSlices,
                      sliceValue,
                      outstanding,
                      clearRate,
                    };
                  })
                  .sort((a, b) => {
                    let aVal, bVal;
                    switch (breadSortField) {
                      case 'rank':
                        // Default ranking by slice value
                        aVal = b.sliceValue - a.sliceValue;
                        bVal = 0;
                        break;
                      case 'sliceValue':
                        aVal = a.sliceValue;  // Sort by calculated lifetime total
                        bVal = b.sliceValue;  // Sort by calculated lifetime total
                        break;
                      case 'outstanding':
                        aVal = a.outstanding;
                        bVal = b.outstanding;
                        break;
                      case 'clearRate':
                        aVal = a.clearRate;
                        bVal = b.clearRate;
                        break;
                      default:
                        aVal = a.sliceValue;
                        bVal = b.sliceValue;
                    }
                    return breadSortDirection === 'asc'
                      ? aVal - bVal
                      : bVal - aVal;
                  })
                  .map((player, index) => (
                    <tr
                      key={player.id}
                      className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50/50 transition-colors"
                    >
                      <td className="py-3 px-4">
                        <div className="w-5 h-5 flex items-center justify-center text-[9px] font-bold rounded bg-zinc-100 text-zinc-600">
                          #{index + 1}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <button
                          onClick={() => {
                            setSelectedPlayer(player);
                            setActiveTab('analytics');
                          }}
                          className="flex items-center gap-2 hover:text-indigo-600 transition-colors cursor-pointer"
                        >
                          <PlayerAvatar initials={player.initials} size="sm" />
                          <span className="font-bold text-zinc-900 text-xs">
                            {player.name}
                          </span>
                        </button>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className="font-mono font-bold text-sm text-zinc-950">
                          {player.outstanding}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className="font-mono font-bold text-sm text-zinc-900">
                          {player.sliceValue.toFixed(1)}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className="font-mono font-bold text-sm text-zinc-900">
                          {player.clearRate}%
                        </span>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default BreadTrackerTab;
